import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

type Kind = 'success' | 'error' | 'info'
interface Toast { id: number; kind: Kind; text: string }

interface ToastApi {
  success: (text: string) => void
  error: (text: string) => void
  info: (text: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<Toast[]>([])
  const seq = useRef(0)

  const push = useCallback((kind: Kind, text: string) => {
    const id = ++seq.current
    setItems((xs) => [...xs, { id, kind, text }])
    window.setTimeout(() => setItems((xs) => xs.filter((t) => t.id !== id)), kind === 'error' ? 6000 : 3500)
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      success: (t) => push('success', t),
      error: (t) => push('error', t),
      info: (t) => push('info', t),
    }),
    [push],
  )

  const tone: Record<Kind, string> = {
    success: 'bg-success-tint text-success border-success/30',
    error: 'bg-danger-tint text-danger border-danger/30',
    info: 'bg-primary-100 text-ink border-primary-200',
  }

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`pointer-events-auto max-w-md rounded-button border px-4 py-2.5 text-sm shadow-float ${tone[t.kind]}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast phải nằm trong <ToastProvider>')
  return ctx
}
